import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert, Box, Button, Checkbox, CircularProgress,
  Divider, FormControlLabel, Stack, Typography,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useCreatePaymentMethodMutation } from 'src/store/Reducer/tilled';
import { ClientApi } from 'src/store/Reducer/client';
import { waitForTilled } from 'src/utils/tilled-service';

// ✅ Correct Tilled field style — single colon for pseudo selectors
const TILLED_FIELD_STYLE = {
  base: {
    fontFamily: "'DM Sans', Helvetica Neue, Arial, sans-serif",
    fontSize: '14px',
    fontWeight: '400',
    color: '#1F1F1F',
    ':placeholder': { color: '#A0A0A0' },  // ← single colon not double
  },
  invalid: {
    color: '#D32F2F',
  },
  valid: {
    color: '#1F1F1F',
  },
};

// ✅ Wrapper box — this controls the visible height and border
const FIELD_BOX_SX = {
  border: '1px solid #E0E0E0',
  borderRadius: '8px',
  px: 1.5,
  minHeight: 44,           // ← controls iframe visible height
  height: 44,              // ← fix it explicitly
  display: 'flex',
  alignItems: 'center',
  bgcolor: 'background.paper',
  overflow: 'hidden',      // ← clip iframe overflow
  transition: 'border-color 0.2s',
  '&:focus-within': {
    borderColor: 'primary.main',
    border: '2px solid',
  },
  // ── Force iframe inside to fill the wrapper ──
  '& iframe': {
    width: '100% !important',
    height: '100% !important',
    minHeight: '44px !important',
  },
};

function FieldWrapper({ label, children, flex }) {
  return (
    <Box sx={{ flex, mb: 1 }}>
      <Typography
        variant="caption"
        fontWeight={500}
        color="text.secondary"
        sx={{
          fontSize: '11px',          // ← smaller label
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          display: 'block',
          mb: 0.5,                   // ← tighter spacing
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

const SaveCardForm = forwardRef(function SaveCardForm(
  {
    customer_id,
    onSuccess,
    merchant_account_id,
    client,
    isCardSaveRequired = false,
    hideSaveCardOption = false,
  },
  _ref
) {
  const [consent, setConsent]     = useState(false);
  const [tilledReady, setTilledReady] = useState(false);
  const [error, setError]         = useState(null);
  const [saved, setSaved]         = useState(false);

  const tilledInstanceRef = useRef(null);
  const tilledFormRef     = useRef(null);

  const PUBLISHABLE_KEY     = process.env.REACT_APP_TILLED_PUBLISHABLE_KEY;
  const MERCHANT_ACCOUNT_ID = merchant_account_id || null;

  const [createPaymentMethodApi, { isLoading }] = useCreatePaymentMethodMutation();
  const dispatch = useDispatch();

  // ── Initialize Tilled hosted fields ───────────────────────────────────────
  useEffect(() => {
    if (!MERCHANT_ACCOUNT_ID || !PUBLISHABLE_KEY) return;
    let cancelled = false;

    const init = async () => {
      try {
        await waitForTilled();
        if (cancelled) return;

        tilledInstanceRef.current = new window.Tilled(
          PUBLISHABLE_KEY,
          MERCHANT_ACCOUNT_ID,
          { sandbox: process.env.REACT_APP_TILLED_SANDBOX === 'true' }
        );

        tilledFormRef.current = await tilledInstanceRef.current.form({ payment_method_type: 'card' });

        const form = tilledFormRef.current;
        form.createField('cardNumber', {
            styles: TILLED_FIELD_STYLE,
            placeholder: 'Card Number',  // ← add this
          }).inject('#tilled-card-number');

          form.createField('cardExpiry', {
            styles: TILLED_FIELD_STYLE,
            placeholder: 'MM / YY',              // ← already working
          }).inject('#tilled-card-expiry');

          form.createField('cardCvv', {
            styles: TILLED_FIELD_STYLE,
            placeholder: 'CVV',                  // ← add this
          }).inject('#tilled-card-cvv');

        await form.build();
        if (!cancelled) setTilledReady(true);
      } catch (err) {
        if (!cancelled) setError('Payment form failed to load. Please refresh the page.');
      }
    };

    init();

    return () => {
      cancelled = true;
      tilledFormRef.current?.teardown?.();
      tilledFormRef.current = null;
      tilledInstanceRef.current = null;
    };
  }, [MERCHANT_ACCOUNT_ID, PUBLISHABLE_KEY]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if ((isCardSaveRequired && !consent) || !tilledReady) return;
    setError(null);

    if (!MERCHANT_ACCOUNT_ID) {
      setError('Missing merchant account');
      return;
    }

    try {
      const confirmedPaymentMethod = await tilledInstanceRef.current.createPaymentMethod({
        type: 'card',
        billing_details: {
          name:    client?.name  || 'Card Holder',
          email:   client?.email || '',
          address: { zip: client?.address?.zip_code || '00000' },
        },
      });


      const pmToken = confirmedPaymentMethod?.id;
      if (!pmToken) throw new Error('Failed to tokenize card.');

      const result = await createPaymentMethodApi({
        merchantAccountId: MERCHANT_ACCOUNT_ID,
        payment_token: pmToken,
        customer_id,
        billing_details: {
          name:    client?.name  || 'Card Holder',
          email:   client?.email || '',
          address: { zip: client?.address?.zip_code || '' },
        },
        client_id: client?._id,
        is_card_saved: consent,
      });

      if (!result?.error) {
        setSaved(true);
        dispatch(ClientApi.util.invalidateTags(['Client']));
        onSuccess?.({ payment_method_id: result?.data?.body?.id, customer_id });
      }
    } catch (err) {
      console.log('Error saving card:', err);
      setError(err?.data?.message || 'Something went wrong. Please try again.');
    }
  }, [consent, tilledReady, customer_id, MERCHANT_ACCOUNT_ID, client, onSuccess, createPaymentMethodApi, isCardSaveRequired, dispatch]);

  if (saved) {
    return (
      <Alert severity="success" icon={<Iconify icon="mdi:check-circle-outline" />} sx={{ mt: 2 }}>
        {consent ? 'Your card has been saved successfully for future payments.' : 'Your card details were captured successfully.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
        <Iconify icon="mdi:lock-outline" sx={{ color: 'primary.main', width: 22, height: 22 }} />
        <Typography variant="h6" fontWeight={600}>Card details</Typography>
        <Box flex={1} />
        <Typography variant="caption" color="text.disabled">Secured by Tilled</Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
        Your card is tokenized via Tilled's PCI-DSS Level 1 hosted fields. We never store or see your card number.
      </Typography>

      <Divider sx={{ mb: 2.5 }} />

      <FieldWrapper label="Card number">
        <Box id="tilled-card-number" sx={FIELD_BOX_SX} />
      </FieldWrapper>

      <Stack direction="row" spacing={2}>
        <FieldWrapper label="Expiry date" flex={1}>
          <Box id="tilled-card-expiry" sx={FIELD_BOX_SX} />
        </FieldWrapper>
        <FieldWrapper label="Security code (CVV)" flex={1}>
          <Box id="tilled-card-cvv" sx={FIELD_BOX_SX} />
        </FieldWrapper>
      </Stack>

      <Divider sx={{ mt: 2.5, mb: 2 }} />

      {!hideSaveCardOption && (
        <FormControlLabel
          sx={{ alignItems: 'flex-start', mb: 2 }}
          control={
            <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} color="primary" size="small" sx={{ mt: 0.25 }} />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              I authorise saving this card for future payments. My card data is stored securely by Tilled and subject to their{' '}
              <a href="https://www.tilled.com/privacy-policy" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                privacy policy
              </a>.
            </Typography>
          }
        />
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Button
        variant="contained"
        fullWidth
        size="large"
        disabled={(isCardSaveRequired && !consent) || !tilledReady || isLoading}
        onClick={handleSave}
        startIcon={!isLoading && <Iconify icon="mdi:credit-card-plus-outline" width={20} />}
        sx={{ py: 1.5 }}
      >
        {isLoading ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          isCardSaveRequired
            ? 'Save Card & Continue'
            : consent
              ? 'Save Card & Continue'
              : 'Add Card'
        )}
      </Button>
    </Box>
  );
});

SaveCardForm.displayName = 'SaveCardForm';
export default SaveCardForm;
